import type { CompanyInfo } from '@scio3/shared-types';
import type { AppVariant } from '@/lib/app-variant';
import { isFreeEmail } from '@/features/survey/utils/free-email-domains';

/**
 * MDM access predicates — shared by `ConnectLayout` (paywall) and
 * `TopNavigation` (Devices tab visibility) so both read one source of truth.
 *
 * `feature.mdm` is the entitlement flag: auto-set to true when an account buys
 * ProPlus / Engage / Enterprise (scio1 payment flow), and also the manual Sales
 * whitelist. It is not yet on the `CompanyFeatures` TS type, so we read it via a
 * narrow structural cast (same approach as `MdmFeatureRoute`).
 */

/**
 * True when the account may USE MDM (no paywall).
 * - optiedge variant: the variant itself is the entitlement (MDM-only product).
 * - optisigns variant: requires `company.feature.mdm === true`.
 */
export function hasMdmEntitlement(company: CompanyInfo | null, variant: AppVariant): boolean {
  if (variant === 'optiedge') return true;
  return (company?.feature as Record<string, unknown> | undefined)?.mdm === true;
}

/**
 * Mass-rollout targeting for the Devices tab (optisigns variant, kill-switch on):
 * the account OWNER has a business email AND the account purchased > 5 screen
 * licenses (`limitation.maxQuantity`, the licensed cap — not the consumed count).
 */
export function passesDevicesTabTargeting(
  company: CompanyInfo | null,
  ownerEmail: string | undefined,
): boolean {
  const isBusinessEmail = !!ownerEmail && !isFreeEmail(ownerEmail);
  const hasMoreThan5Screens = (company?.limitation?.maxQuantity ?? 0) > 5;
  return isBusinessEmail && hasMoreThan5Screens;
}

/** Per-section paywall copy for the MDM area. `featureLabel` is an i18n key. */
export interface MdmPaywallCopy {
  featureLabel: string;
  learnMoreUrl: string;
}

const MDM_PRICING_URL = 'https://www.optisigns.com/pricing';
// Deep link kept from the original Provisioning/Wi-Fi in-page gates.
const MASS_PROVISION_LEARN_MORE = 'https://links.optisigns.com/4mELQ5y';
const ROOM_INTEGRATION_LEARN_MORE = 'https://links.optisigns.com/4g8VRaQ';

/**
 * Pick the paywall message + Learn-more URL for the current MDM route, so the
 * single ConnectLayout PlanGate reads correctly per section (Manage / Room
 * Integration / Provisioning Templates / Wi-Fi & 802.1X). Falls back to the
 * Manage copy for the Devices/Fleets index.
 */
export function mdmPaywallCopy(pathname: string): MdmPaywallCopy {
  if (pathname.startsWith('/app/mdm/connect')) {
    return { featureLabel: 'Room Integrations', learnMoreUrl: ROOM_INTEGRATION_LEARN_MORE };
  }
  if (pathname.startsWith('/app/mdm/templates')) {
    return { featureLabel: 'Provisioning Templates', learnMoreUrl: MASS_PROVISION_LEARN_MORE };
  }
  if (pathname.startsWith('/app/mdm/wifi-profiles')) {
    return { featureLabel: 'wifi_profiles.page.title', learnMoreUrl: MASS_PROVISION_LEARN_MORE };
  }
  // Manage — Devices (/app/mdm) + Fleets (/app/mdm/fleets)
  return { featureLabel: 'Device Management', learnMoreUrl: MDM_PRICING_URL };
}
